import { z } from "zod";
import { optionalDescriptionSchema } from "./common";

/**
 * Aggregate validation schemas
 * Backend-aligned schemas that ensure type safety with generated API types
 */

// Backend type definitions for type safety (for documentation)
// type BackendAggregateCreate = components['schemas']['AggregateCreate'];
// type BackendAggregateUpdate = components['schemas']['AggregateUpdate'];

/**
 * Aggregate type schema - accepts any string from the backend
 * @deprecated Use backendAggregateTypeSchema for validation
 */
export const aggregateTypeSchema = z
  .string()
  .min(1, "Aggregate type is required");

/**
 * Aggregate source schema - accepts any string from the backend
 * @deprecated Use backendSourceSchema for validation
 */
export const aggregateSourceSchema = z.string().min(1, "Source is required");

/**
 * Aggregate name validation schema (specialized)
 */
export const aggregateNameSchema = z
  .string()
  .min(2, "Aggregate name must be at least 2 characters long")
  .max(255, "Aggregate name must be less than 255 characters")
  .trim();

/**
 * Backend-aligned aggregate type schema
 * Backend is the source of truth; values are fetched dynamically via /api/v1/aggregate/types
 */
export const backendAggregateTypeSchema = z
  .string()
  .min(1, "Aggregate type is required");

/**
 * Backend-aligned standard size schema
 * Backend is the source of truth; values are fetched dynamically via /api/v1/aggregate/standard-sizes
 */
export const backendStandardSizeSchema = z.string().nullable();

/**
 * Backend-aligned source schema
 * Backend is the source of truth; values are fetched dynamically via /api/v1/aggregate/sources
 */
export const backendSourceSchema = z.string().min(1, "Source is required");

/**
 * Material code on the aggregate (unique per client, shared by every facility).
 * Surrounding space is ignored; an empty value means "no code".
 */
export const materialCodeSchema = z
  .string()
  .trim()
  .max(30, "Must be 30 characters or less")
  .refine(
    (value) => value.length === 0 || value.length >= 2,
    "Must be at least 2 characters",
  );

/** Trimmed code, or null when blank. */
export function normalizeMaterialCode(
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Schema for creating a new aggregate (OpenAPI AggregateCreate).
 * `client_id` is a query param on POST /api/v1/aggregate, not a body field.
 */
export const aggregateCreateSchema = z.object({
  name: aggregateNameSchema,
  agg_type: backendAggregateTypeSchema,
  standard_size: backendStandardSizeSchema.optional().nullable(),
  source: backendSourceSchema.optional().nullable(),
  mineralogy: optionalDescriptionSchema.optional().nullable(),
  description: optionalDescriptionSchema.optional().nullable(),
  material_code: materialCodeSchema.optional().nullable(),
});

/**
 * Schema for updating an aggregate (backend-aligned)
 */
export const aggregateUpdateSchema = z
  .object({
    name: aggregateNameSchema.optional(),
    agg_type: backendAggregateTypeSchema.optional(),
    standard_size: backendStandardSizeSchema.optional().nullable(),
    source: backendSourceSchema.optional().nullable(),
    mineralogy: optionalDescriptionSchema.optional().nullable(),
    description: optionalDescriptionSchema.optional().nullable(),
    material_code: materialCodeSchema.optional().nullable(),
  })
  .refine(
    (data) => Object.keys(data).length > 0,
    "At least one field must be provided for update",
  );

// Type inference for aggregate schemas (backend-aligned)
export type AggregateCreateForm = z.infer<typeof aggregateCreateSchema>;
export type AggregateUpdateForm = z.infer<typeof aggregateUpdateSchema>;

/**
 * Forms use full Zod schemas with useFormValidation hook for validation.
 * Backend (FastAPI/Pydantic) is the source of truth for validation.
 */

/** Alias matching OpenAPI AggregateCreate (no client_id in the body). */
export const backendCreateAggregateSchema = aggregateCreateSchema;

// Alias for backward compatibility
export { backendCreateAggregateSchema as createAggregateSchema };

/**
 * Location aggregate status enum (matches backend exactly)
 */
export const locationAggregateStatusSchema = z.enum([
  "In Use",
  "Not In Use",
  "Archived",
]);

/**
 * Schema for assigning a location to an aggregate.
 * The material code lives on the aggregate, so an assignment only carries status.
 */
export const locationAssignmentSchema = z.object({
  current_status: locationAggregateStatusSchema,
});

// Type inference for location assignment
export type LocationAssignmentForm = z.infer<typeof locationAssignmentSchema>;
export type LocationAggregateStatusType = z.infer<
  typeof locationAggregateStatusSchema
>;
